'use client';

import { useEffect, useState } from 'react';
import { useParams, useRouter } from 'next/navigation';
import { useTranslation } from '@/hooks/useTranslation';
import { usePermissions } from '@/hooks/usePermissions';
import { toast } from '@/lib/toast';
import { 
  getPositions,
  getUsers,
  type Position,
  type User,
} from '@/services/api/organization';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PermissionGuard } from '@/components/common/PermissionGuard';
import {
  ArrowLeft,
  Briefcase,
  Users as UsersIcon,
  Loader2,
  Mail,
  Phone,
  Building2,
} from 'lucide-react';

export default function PositionDetailPage() {
  const params = useParams();
  const router = useRouter();
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const positionId = params.id as string;

  // State
  const [position, setPosition] = useState<Position | null>(null);
  const [employees, setEmployees] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);

  // Load data
  useEffect(() => {
    loadPositionData();
  }, [positionId]);

  const loadPositionData = async () => {
    try {
      setLoading(true);
      
      // 加载所有岗位以找到当前岗位
      const positions = await getPositions();
      const currentPosition = positions.find(p => p.id === positionId);
      
      if (!currentPosition) {
        setPosition(null);
        return;
      }
      
      setPosition(currentPosition);
      
      // 加载该岗位的所有用户
      const allUsers = await getUsers();
      const positionEmployees = allUsers.items.filter(user => 
        user.position?.id === positionId || 
        user.departmentMemberships?.some(dm => dm.position?.id === positionId)
      );
      setEmployees(positionEmployees);
    } catch (error) {
      console.error('Failed to load position data:', error);
      toast.error(t.organization.positions.loadFailed);
    } finally {
      setLoading(false);
    }
  };

  // Get level display
  const getLevelDisplay = (level?: number) => {
    if (!level) return '-';
    return `Level ${level}`;
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-500">{t.common.loading}</p>
        </div>
      </div>
    );
  }

  // Not found state
  if (!position) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Briefcase className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            {t.organization.positions.notFound}
          </h2>
          <Button onClick={() => router.back()} variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t.common.back}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <PermissionGuard permissions={['position:read']}>
      <div className="container mx-auto p-6 max-w-7xl">
        {/* Header */}
        <div className="mb-6">
          <Button onClick={() => router.back()} variant="ghost" className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t.common.back}
          </Button>
          
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-bold text-gray-900">{position.name}</h1>
                {position.code && (
                  <Badge variant="secondary" className="text-sm">
                    {position.code}
                  </Badge>
                )}
                {position.level && (
                  <Badge variant="outline" className="text-sm">
                    {getLevelDisplay(position.level)}
                  </Badge>
                )}
              </div>
              {position.description && (
                <p className="text-gray-500 mt-1">{position.description}</p>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="basic" className="space-y-4">
          <TabsList>
            <TabsTrigger value="basic">
              <Briefcase className="h-4 w-4 mr-2" />
              {t.organization.basicInfo}
            </TabsTrigger>
            <TabsTrigger value="employees">
              <UsersIcon className="h-4 w-4 mr-2" />
              {t.organization.positions.employees} ({employees.length})
            </TabsTrigger>
          </TabsList>

          {/* Basic Info Tab */}
          <TabsContent value="basic">
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4">
                {t.organization.basicInfo}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm text-gray-500">
                    {t.organization.positions.name}
                  </label>
                  <p className="mt-1 text-gray-900">{position.name}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">
                    {t.organization.positions.code}
                  </label>
                  <p className="mt-1 text-gray-900">{position.code || '-'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">
                    {t.organization.positions.level}
                  </label>
                  <p className="mt-1 text-gray-900">{getLevelDisplay(position.level)}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">
                    {t.organization.positions.employeeCount}
                  </label>
                  <p className="mt-1 text-gray-900">{employees.length}</p>
                </div>
                {position.description && (
                  <div className="md:col-span-2">
                    <label className="text-sm text-gray-500">
                      {t.organization.description}
                    </label>
                    <p className="mt-1 text-gray-900">{position.description}</p>
                  </div>
                )}
                <div>
                  <label className="text-sm text-gray-500">
                    {t.common.createdAt}
                  </label>
                  <p className="mt-1 text-gray-900">
                    {new Date(position.createdAt).toLocaleString()}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">
                    {t.common.updatedAt}
                  </label>
                  <p className="mt-1 text-gray-900">
                    {new Date(position.updatedAt).toLocaleString()}
                  </p>
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Employees Tab */}
          <TabsContent value="employees">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">
                  {t.organization.positions.employees}
                </h2>
              </div>

              {employees.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  {t.organization.positions.noEmployees}
                </div>
              ) : (
                <div className="space-y-3">
                  {employees.map((employee) => (
                    <div
                      key={employee.id}
                      className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => router.push(`/organization/members/${employee.id}`)}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        {/* Avatar */}
                        <div className="flex-shrink-0">
                          <div className="h-10 w-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold">
                            {employee.displayName?.[0] || employee.username?.[0] || 'U'}
                          </div>
                        </div>
                        
                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">
                              {employee.displayName || employee.username}
                            </span>
                            {employee.employeeId && (
                              <Badge variant="outline" className="text-xs">
                                {employee.employeeId}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                            {employee.department && (
                              <span className="flex items-center gap-1">
                                <Building2 className="h-3 w-3" />
                                {employee.department.name}
                              </span>
                            )}
                            {employee.email && (
                              <span className="flex items-center gap-1">
                                <Mail className="h-3 w-3" />
                                {employee.email}
                              </span>
                            )}
                            {employee.phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="h-3 w-3" />
                                {employee.phone}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* Status */}
                        <div>
                          <Badge 
                            className={
                              employee.status === 'ACTIVE' 
                                ? 'bg-green-100 text-green-800 border-green-200' 
                                : 'bg-gray-100 text-gray-800 border-gray-200'
                            }
                          >
                            {employee.status}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </PermissionGuard>
  );
}

